import React, { useRef, useState } from "react";
import { Trash2, Edit3, Save, X, Camera, Package, MapPin, Tag } from "lucide-react";

const CATEGORIES = ["tools", "electronics", "supplies", "documents", "other"];

const readAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
    });

export const InventoryCard = ({ item, onUpdate, onDelete }) => {
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState(item);
    const fileRef = useRef(null);

    const save = async () => {
        const patch = {};
        ["name", "bin", "category", "notes", "photo"].forEach((k) => {
            if (form[k] !== item[k]) patch[k] = form[k];
        });
        if (Object.keys(patch).length) await onUpdate(item.id, patch);
        setEditing(false);
    };

    const setPhoto = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const data = await readAsDataUrl(f);
        setForm((s) => ({ ...s, photo: data }));
        if (fileRef.current) fileRef.current.value = "";
    };

    if (!editing) {
        return (
            <div
                className="bg-meye-card border border-meye-border rounded-md p-3 fade-up flex gap-3"
                data-testid={`inventory-card-${item.id}`}
            >
                <div className="w-16 h-16 flex-shrink-0 rounded border border-meye-border bg-black/40 overflow-hidden flex items-center justify-center">
                    {item.photo ? (
                        <img src={item.photo} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                        <Package size={22} className="text-zinc-600" strokeWidth={1.5} />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-brand font-bold text-zinc-100 truncate">{item.name}</p>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                                onClick={() => setEditing(true)}
                                className="text-zinc-500 hover:text-meye-cyan"
                                data-testid={`inventory-edit-${item.id}`}
                            >
                                <Edit3 size={13} />
                            </button>
                            <button
                                onClick={() => onDelete(item.id)}
                                className="text-zinc-500 hover:text-red-400"
                                data-testid={`inventory-delete-${item.id}`}
                            >
                                <Trash2 size={13} />
                            </button>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[10px] uppercase tracking-widest font-mono">
                        {item.bin && (
                            <span className="text-meye-cyan flex items-center gap-1">
                                <MapPin size={10} /> {item.bin}
                            </span>
                        )}
                        {item.category && item.category !== "other" && (
                            <span className="text-meye-fuchsia flex items-center gap-1">
                                <Tag size={10} /> {item.category}
                            </span>
                        )}
                    </div>
                    {item.notes && <p className="text-xs text-zinc-400 mt-1.5 line-clamp-2">{item.notes}</p>}
                </div>
            </div>
        );
    }

    return (
        <div className="bg-meye-card border border-meye-cyan/40 rounded-md p-3 fade-up space-y-2" data-testid={`inventory-edit-form-${item.id}`}>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={setPhoto} />
            <div className="flex gap-3">
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="w-16 h-16 flex-shrink-0 rounded border border-dashed border-meye-border bg-black/40 overflow-hidden flex items-center justify-center hover:border-meye-cyan"
                    data-testid={`inventory-photo-${item.id}`}
                >
                    {form.photo ? (
                        <img src={form.photo} alt="" className="w-full h-full object-cover" />
                    ) : (
                        <Camera size={20} className="text-zinc-500" />
                    )}
                </button>
                <div className="flex-1 space-y-2">
                    <input
                        value={form.name || ""}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="name"
                        className="w-full bg-black/40 border border-meye-border rounded p-1.5 text-sm text-zinc-100 font-mono focus:outline-none focus:border-meye-cyan/60"
                        data-testid={`inventory-name-${item.id}`}
                    />
                    <input
                        value={form.bin || ""}
                        onChange={(e) => setForm({ ...form, bin: e.target.value })}
                        placeholder="bin / location"
                        className="w-full bg-black/40 border border-meye-border rounded p-1.5 text-xs text-zinc-100 font-mono focus:outline-none focus:border-meye-cyan/60"
                        data-testid={`inventory-bin-${item.id}`}
                    />
                </div>
            </div>
            <select
                value={form.category || "other"}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full bg-black/40 border border-meye-border rounded p-1.5 text-xs text-zinc-200 font-mono"
                data-testid={`inventory-category-${item.id}`}
            >
                {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                ))}
            </select>
            <textarea
                value={form.notes || ""}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="notes"
                rows={2}
                className="w-full bg-black/40 border border-meye-border rounded p-1.5 text-xs text-zinc-100 font-mono focus:outline-none focus:border-meye-cyan/60"
                data-testid={`inventory-notes-${item.id}`}
            />
            <div className="flex justify-end gap-3 text-xs">
                <button
                    onClick={() => {
                        setForm(item);
                        setEditing(false);
                    }}
                    className="text-zinc-500 hover:text-white"
                >
                    <X size={13} />
                </button>
                <button
                    onClick={save}
                    className="text-meye-cyan hover:text-cyan-300 flex items-center gap-1"
                    data-testid={`inventory-save-${item.id}`}
                >
                    <Save size={13} /> save
                </button>
            </div>
        </div>
    );
};

export const InventoryAddForm = ({ onCreate }) => {
    const [name, setName] = useState("");
    const [bin, setBin] = useState("");
    const [category, setCategory] = useState("other");
    const [photo, setPhoto] = useState(null);
    const fileRef = useRef(null);

    const submit = async () => {
        if (!name.trim()) return;
        await onCreate({ name: name.trim(), bin: bin.trim(), category, photo });
        setName("");
        setBin("");
        setCategory("other");
        setPhoto(null);
    };

    const pickPhoto = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setPhoto(await readAsDataUrl(f));
        if (fileRef.current) fileRef.current.value = "";
    };

    return (
        <div className="bg-meye-card border border-meye-border rounded-md p-3 space-y-2" data-testid="inventory-add-form">
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={pickPhoto} />
            <div className="flex gap-2">
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="w-14 h-14 flex-shrink-0 rounded border border-dashed border-meye-border bg-black/40 overflow-hidden flex items-center justify-center hover:border-meye-cyan"
                    data-testid="inventory-add-photo"
                >
                    {photo ? (
                        <img src={photo} alt="" className="w-full h-full object-cover" />
                    ) : (
                        <Camera size={18} className="text-zinc-500" />
                    )}
                </button>
                <div className="flex-1 space-y-2">
                    <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="item name..."
                        className="w-full bg-black/40 border border-meye-border rounded px-2 py-1.5 text-sm text-zinc-100 font-mono focus:outline-none focus:border-meye-fuchsia/50"
                        data-testid="inventory-add-name"
                    />
                    <div className="flex gap-2">
                        <input
                            value={bin}
                            onChange={(e) => setBin(e.target.value)}
                            placeholder="bin / location"
                            className="flex-1 bg-black/40 border border-meye-border rounded px-2 py-1.5 text-xs text-zinc-100 font-mono focus:outline-none focus:border-meye-cyan/60"
                            data-testid="inventory-add-bin"
                        />
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="bg-black/40 border border-meye-border rounded px-2 py-1.5 text-xs text-zinc-200 font-mono"
                            data-testid="inventory-add-category"
                        >
                            {CATEGORIES.map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
            <div className="flex justify-end">
                <button
                    onClick={submit}
                    disabled={!name.trim()}
                    className="bg-meye-fuchsia hover:bg-fuchsia-500 disabled:opacity-40 text-black rounded px-3 py-1.5 text-[11px] font-brand font-bold uppercase tracking-widest"
                    data-testid="inventory-add-submit"
                >
                    log item
                </button>
            </div>
        </div>
    );
};

export default InventoryCard;
